/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { Comparison } from '@sembo-private/comparisons';
import {
  booleanComparer,
  compare,
  localeStringComparer,
  on,
  onLocaleString,
  onNumber,
  reversed,
} from '@sembo-private/comparisons';

import type {
  CancellationPolicy,
  ItineraryIngredientResponse,
  HotelBookableAndLockableOption,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';

export const bookableOptionComparer = compare<HotelBookableAndLockableOption>(
  reversed((a, b) => booleanComparer(a.isLocked, b.isLocked)),
  reversed((a, b) => booleanComparer(a.isFromMeta, b.isFromMeta)),
  onNumber((a) => a.totalPrice.amount)
);

export const popularityAirportComparer = compare<{
  airport: { popularity: number } | null;
}>(reversed(onNumber((item) => item.airport?.popularity ?? 0)));

export function ingredientDateComparer(
  a: ItineraryIngredientResponse,
  b: ItineraryIngredientResponse,
  isTransport: (ingredient: ItineraryIngredientResponse) => boolean
): number {
  const semboDateA = new SemboDate(a.date);
  const semboDateB = new SemboDate(b.date);

  if (semboDateA.isSameDay(semboDateB.get())) {
    return isTransport(a) ? -1 : 1;
  }
  return semboDateA.isAfter(semboDateB.startOf('day').get()) ? 1 : -1;
}

export const stringComparer = compare(on(JSON.stringify, localeStringComparer));

export function cnxCompare(
  cnxA: CancellationPolicy,
  cnxB: CancellationPolicy
): number {
  return dateCompare(cnxA.fromDate, cnxB.fromDate);
}

export function propertyStringComparer<T>(
  propertySelector: (item: T) => string
): Comparison<T> {
  return compare<T>(onLocaleString(propertySelector));
}

export function dateCompare(
  dateA: Date | string,
  dateB: Date | string
): number {
  const semboDateA = new SemboDate(dateA);
  const semboDateB = new SemboDate(dateB);
  if (semboDateA.isSame(semboDateB.get())) {
    return 0;
  }
  return semboDateA.isAfter(semboDateB.get()) ? 1 : -1;
}
